import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';

interface PendingClaimsSelectionContextValue {
    selectedIds: string[];
    isSelected: (id: string) => boolean;
    toggleSelection: (id: string) => void;
    selectAll: (ids: string[]) => void;
    clearSelection: () => void;
    isAllSelected: (allIds: string[]) => boolean;
}

const PendingClaimsSelectionContext = createContext<PendingClaimsSelectionContextValue | undefined>(undefined);

interface PendingClaimsSelectionProviderProps {
    children: ReactNode;
}

export function PendingClaimsSelectionProvider({ children }: PendingClaimsSelectionProviderProps) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const isSelected = useCallback(
        (id: string) => {
            return selectedIds.includes(id);
        },
        [selectedIds]
    );

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
    }, []);

    const selectAll = useCallback((ids: string[]) => {
        setSelectedIds(ids);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const isAllSelected = useCallback(
        (allIds: string[]) => {
            if (allIds.length === 0) {
                return false;
            }
            return allIds.every((id) => selectedIds.includes(id));
        },
        [selectedIds]
    );

    return (
        <PendingClaimsSelectionContext.Provider
            value={{
                selectedIds,
                isSelected,
                toggleSelection,
                selectAll,
                clearSelection,
                isAllSelected,
            }}
        >
            {children}
        </PendingClaimsSelectionContext.Provider>
    );
}

export function usePendingClaimsSelection() {
    const context = useContext(PendingClaimsSelectionContext);
    if (context === undefined) {
        throw new Error('usePendingClaimsSelection must be used within a PendingClaimsSelectionProvider');
    }
    return context;
}
